import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { ClipboardIcon, CheckIcon, SparklesIcon, PhoneIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { doc, getDoc, updateDoc, serverTimestamp, collection, addDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import PhoneVerificationModal from '../PhoneVerificationModal';

const PhoneNumberSuccessModal = ({ isOpen, onClose, phoneNumber, country, onRefresh }) => {
  const [copied, setCopied] = useState(false);
  const [isRare, setIsRare] = useState(false);
  const [rareMessage, setRareMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [activationType, setActivationType] = useState(null); // 'forward' or 'voip'
  const [showActivationOptions, setShowActivationOptions] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Check if the number is rare when modal opens
  useEffect(() => {
    const checkRareStatus = async () => {
      if (!phoneNumber || !isOpen) return;
      
      setLoading(true);
      try {
        // Get the number document from userNumbers collection
        const numberDoc = await getDoc(doc(db, 'userNumbers', phoneNumber));
        
        if (numberDoc.exists()) {
          const data = numberDoc.data();
          setIsRare(data.isRare === true || data.isRare === 'TRUE');
          setRareMessage(data.rareMessage || '');
        }
      } catch (error) {
        console.error('Error checking rare status:', error);
        // Default to false if there's an error
        setIsRare(false);
        setRareMessage('');
      } finally {
        setLoading(false);
      }
    };

    checkRareStatus();
  }, [phoneNumber, isOpen]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(phoneNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const formatPhoneNumber = (number) => {
    if (!number) return '';
    // Remove the + and format based on country
    const cleaned = number.replace(/\D/g, '');
    if (country === 'US' || country === 'CA') {
      if (cleaned.length === 11 && cleaned.startsWith('1')) {
        const areaCode = cleaned.slice(1, 4);
        const prefix = cleaned.slice(4, 7);
        const suffix = cleaned.slice(7, 11);
        return `+1 (${areaCode}) ${prefix}-${suffix}`;
      }
    }
    return number; // Return original if formatting fails
  };

  // Function to initiate the Stripe checkout session (same as PhoneRelayDetails)
  const initiateStripeCheckout = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert("User not authenticated");
        throw new Error("User not authenticated");
      }
      const checkoutSessionsRef = collection(db, "userAliases", currentUser.uid, "checkout_sessions");
      const sessionDocRef = await addDoc(checkoutSessionsRef, {
        price: "price_1R7o5rK5A174OlbcwkSlu9cl",
        trial_period_days: 30,
        success_url: "https://relay.chatodyssey.com/overview?session_id={CHECKOUT_SESSION_ID}",
        cancel_url: "https://relay.chatodyssey.com/overview",
      });

      onSnapshot(sessionDocRef, (snap) => {
        const data = snap.data();
        if (!data) return;
        const { error, url } = data;
        if (error) {
          alert(`An error occurred: ${error.message}`);
          console.error("Checkout session error:", error);
        }
        if (url) {
          window.location.assign(url);
        }
      });
    } catch (error) {
      console.error("Error initiating Stripe checkout:", error);
      alert("Error initiating upgrade");
      throw error;
    }
  };

  // Activate the phone relay with the selected type
  const handleActivate = async (type) => {
    setActivating(true);
    setActivationType(type);
    
    try {
      const uid = auth.currentUser.uid;
      
      // Get the phone alias document to get telnyxID
      const phoneAliasRef = doc(db, 'userAliases', uid, 'phoneAliases', phoneNumber);
      const phoneAliasDoc = await getDoc(phoneAliasRef);
      
      if (!phoneAliasDoc.exists()) {
        throw new Error('Phone alias not found');
      }
      
      const phoneAliasData = phoneAliasDoc.data();
      const telnyxID = phoneAliasData.telnyxID;
      
      // Check premium eligibility
      const eligibilityResponse = await fetch('/api/checkPremiumEligibility', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      });
      
      let eligibilityData;
      try {
        eligibilityData = await eligibilityResponse.json();
      } catch (e) {
        console.error('Error parsing eligibility response:', e);
        throw new Error('Invalid response from eligibility check');
      }
      
      if (!eligibilityData.eligible) {
        console.log("User not premium. Initiating Stripe checkout...");
        await initiateStripeCheckout();
        setActivating(false);
        return;
      }
      
      if (type === 'forward') {
        // For forwarding, we need to check if user has a forwardToNumber
        // If not, we'll show the verification modal
        if (!phoneAliasData.forwardTo || phoneAliasData.forwardTo === '') {
          console.log('No forwarding number set, showing verification modal');
          setShowVerificationModal(true);
          setActivating(false);
          return;
        }
        
        // Proceed with call forwarding
        const forwardResponse = await fetch('/api/telnyx/forwardCall', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            telnyxID: telnyxID,
            forwardToNumber: phoneAliasData.forwardTo,
          }),
        });

        let forwardData;
        try {
          const text = await forwardResponse.text();
          console.log('Forward response text:', text);
          forwardData = JSON.parse(text);
        } catch (e) {
          console.error('Error parsing forward response:', e);
          throw new Error('Invalid response from forward call');
        }

        if (!forwardResponse.ok) {
          throw new Error(forwardData.error || forwardData.details?.error || 'Failed to forward call');
        }
        console.log('Call forwarding activated successfully:', forwardData);
      } else {
        // Activate VOIP/messaging functionality
        console.log('Activating messaging for:', telnyxID);
        
        const messagingResponse = await fetch('/api/telnyx/activateMessaging', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            telnyxID: telnyxID
          }),
          credentials: 'same-origin'
        });

        let messagingData;
        try {
          const text = await messagingResponse.text();
          console.log('Messaging response text:', text);
          if (!text) {
            throw new Error('Empty response from server');
          }
          messagingData = JSON.parse(text);
        } catch (e) {
          console.error('Error parsing messaging response:', e);
          throw new Error(`Invalid response from messaging activation: ${e.message}`);
        }

        if (!messagingResponse.ok || !messagingData.success) {
          const error = messagingData.error || messagingData.details?.error || 'Failed to activate messaging';
          console.error('Messaging activation failed:', messagingData);
          throw new Error(error);
        }
        console.log('Messaging activated successfully:', messagingData);
        
        // Assign the phone number to a 10DLC campaign
        console.log('Assigning phone number to 10DLC campaign:', phoneNumber);
        
        const tenDlcResponse = await fetch('/api/telnyx/assign10DLCCampaign', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            phoneNumber: phoneNumber,
            campaignId: process.env.NEXT_PUBLIC_TELNYX_10DLC_CAMPAIGN_ID || '4b300195-d896-b518-74e2-24c74e4aa60d'
          }),
          credentials: 'same-origin'
        });

        let tenDlcData;
        try {
          const text = await tenDlcResponse.text();
          console.log('10DLC response text:', text);
          if (text) {
            tenDlcData = JSON.parse(text);
          }
        } catch (e) {
          console.error('Error parsing 10DLC response:', e);
          console.warn(`10DLC campaign assignment issue: ${e.message}`);
        }

        if (!tenDlcResponse.ok) {
          console.warn('10DLC campaign assignment unsuccessful:', tenDlcData?.error || tenDlcData?.details?.error || 'Unknown error');
        } else {
          console.log('10DLC campaign assigned successfully:', tenDlcData);
        }
      }
      
      // Update connectionStatus to "Active" in the phoneAliases subcollection
      const updateData = {
        connectionStatus: 'Active',
        updatedAt: serverTimestamp()
      };
      
      // Add type field for voip
      if (type === 'voip') {
        updateData.type = 'voip';
      }
      
      await updateDoc(phoneAliasRef, updateData);

      // If it's VOIP type and activation was successful, redirect to messaging
      if (type === 'voip') {
        window.location.href = '/dashboard/trace';
      } else {
        // Close modal and refresh the phone relay list
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      }
    } catch (error) {
      console.error('Error activating phone relay:', error);
      alert(`Failed to activate: ${error.message}`);
    } finally {
      setActivating(false);
      setActivationType(null);
    }
  };

  const handleActivateNow = () => {
    setShowActivationOptions(true);
  };

  const handleActivateLater = () => {
    onClose();
  };

  // Handle phone verification completion
  const handlePhoneVerified = async (verifiedPhone) => {
    console.log("Phone verified:", verifiedPhone);
    setShowVerificationModal(false);
    
    try {
      // Update the phone alias with the verified forwarding number
      const uid = auth.currentUser.uid;
      const phoneAliasRef = doc(db, 'userAliases', uid, 'phoneAliases', phoneNumber);
      await updateDoc(phoneAliasRef, {
        forwardTo: verifiedPhone,
        updatedAt: serverTimestamp()
      });
      
      // Now proceed with activation
      await handleActivate('forward');
    } catch (error) {
      console.error('Error updating forwarding number:', error);
      alert(`Failed to update forwarding number: ${error.message}`);
    }
  };

  // Handle verification modal close
  const handleVerificationClose = () => {
    setShowVerificationModal(false);
    setActivationType(null);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="mx-auto max-w-md w-full">
            {/* Liquid Glass Panel */}
            <div 
              className="relative bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 40px rgba(0,0,0,0.3)'
              }}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
              >
                <span className="text-lg">×</span>
              </button>

              {/* Header */}
              <div className="mb-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckIcon className="h-8 w-8 text-green-400" />
                </div>
                <h2 className="text-xl font-medium text-white mb-2">
                  Number Assigned!
                </h2>
                <p className="text-white/60 text-sm">
                  Your new phone number is ready
                </p>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 rounded-full border-2 border-white/20 border-t-white/60 animate-spin mx-auto mb-4"></div>
                  <p className="text-white/60 text-sm">Checking number details...</p>
                </div>
              ) : (
                <>
                  {/* Rare Number Alert */}
                  {isRare && (
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30">
                      <div className="flex items-start gap-3">
                        <SparklesIcon className="h-6 w-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="text-yellow-400 font-medium mb-2">
                            🎉 Congratulations! High Value Number
                          </h3>
                          <p className="text-white/80 text-sm mb-2">
                            You have been randomly selected to receive a very high value phone number at no additional cost!
                          </p>
                          {rareMessage && (
                            <p className="text-white/70 text-sm italic">
                              {rareMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Phone Number Display */}
                  <div className="mb-6">
                    <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-white/60 text-xs mb-1">Your Number</p>
                          <p className="text-white text-lg font-mono font-medium">
                            {formatPhoneNumber(phoneNumber)}
                          </p>
                        </div>
                        <button
                          onClick={handleCopy}
                          className="ml-3 p-2 rounded-lg bg-white/10 border border-white/20 text-white/70 hover:text-white hover:bg-white/20 transition-all"
                          title="Copy number"
                        >
                          {copied ? (
                            <CheckIcon className="h-5 w-5 text-green-400" />
                          ) : (
                            <ClipboardIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Activation Options */}
                  {!showActivationOptions ? (
                    <>
                      {/* Initial Options */}
                      <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                        <h3 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                          Ready to Activate?
                        </h3>
                        <p className="text-white/80 text-sm">
                          Your number is ready! You can activate it now or set it up later from your dashboard.
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-3">
                        <button
                          onClick={handleActivateNow}
                          className="w-full py-3 rounded-xl bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 font-medium hover:bg-indigo-500/30 transition-all"
                        >
                          Activate Now
                        </button>
                        <button
                          onClick={handleActivateLater}
                          className="w-full py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 font-medium hover:bg-white/10 hover:text-white/80 transition-all text-sm"
                        >
                          I'll activate later
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Activation Type Selection */}
                      <div className="mb-6">
                        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                          <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
                          Choose Activation Type
                        </h3>
                        <div className="space-y-3">
                          {/* Forwarding Option */}
                          <button
                            onClick={() => handleActivate('forward')}
                            disabled={activating}
                            className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-green-500/20 backdrop-blur border border-green-400/30 flex items-center justify-center">
                                <PhoneIcon className="w-6 h-6 text-green-400" />
                              </div>
                              <div className="flex-1 text-left">
                                <div className="text-white font-medium mb-1">
                                  {activating && activationType === 'forward' ? (
                                    <div className="flex items-center gap-2">
                                      <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin"></div>
                                      Activating Forwarding...
                                    </div>
                                  ) : (
                                    'Call Forwarding'
                                  )}
                                </div>
                                <p className="text-white/60 text-sm">
                                  Route calls and texts to your existing phone number
                                </p>
                              </div>
                            </div>
                          </button>

                          {/* Virtual Number Option */}
                          <button
                            onClick={() => handleActivate('voip')}
                            disabled={activating}
                            className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-indigo-500/20 backdrop-blur border border-indigo-400/30 flex items-center justify-center">
                                <ChatBubbleLeftRightIcon className="w-6 h-6 text-indigo-400" />
                              </div>
                              <div className="flex-1 text-left">
                                <div className="text-white font-medium mb-1">
                                  {activating && activationType === 'voip' ? (
                                    <div className="flex items-center gap-2">
                                      <div className="w-4 h-4 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin"></div>
                                      Activating Virtual Number...
                                    </div>
                                  ) : (
                                    'Virtual Number'
                                  )}
                                </div>
                                <p className="text-white/60 text-sm">
                                  Send and receive messages directly in this app
                                </p>
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Back Button */}
                      <button
                        onClick={() => setShowActivationOptions(false)}
                        disabled={activating}
                        className="w-full py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 font-medium hover:bg-white/10 hover:text-white/80 transition-all text-sm disabled:opacity-50"
                      >
                        Back
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
      
      {/* Phone Verification Modal */}
      {showVerificationModal && (
        <PhoneVerificationModal 
          uid={auth.currentUser?.uid} 
          onVerified={handlePhoneVerified} 
          onClose={handleVerificationClose} 
        />
      )}
    </Dialog>
  );
};

PhoneNumberSuccessModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  phoneNumber: PropTypes.string,
  country: PropTypes.string,
  onRefresh: PropTypes.func
};

export default PhoneNumberSuccessModal; 